import { Avatar, Button, type ColumnDef, DataTable, EmptyState, Input, StatusLabel, WorkOrderIcon } from '@olytic/foundation';
import { useMemo, useState } from 'react';

import { PlusGlyph, PriorityMark, TypeMark } from './marks';
import {
  assigneeOptions, assigneePatch, assigneeValue, dueState, type Filters, filtersActive, type GroupKey, groupsFor, hoursText, isBlocked, type Lookups, matches,
  PRIORITIES, PRIORITY_LABEL, shortDate, STATUS_LABEL, STATUSES, todayIso, treeRows,
} from './model';
import { PERSON_DRAG, type AvatarOf, type Patch } from './WorkPage';
import type { WorkBootstrap, WorkItem, WorkPriority, WorkProject, WorkStatus } from './workApi';

const TONE: Record<WorkStatus, 'neutral' | 'accent' | 'warning' | 'success'> = Object.fromEntries(STATUSES.map((s) => [s.key, s.tone])) as never;

/** The List: the epic, task and subtask tree, or sections when grouped, with inline edits. */
export function WorkList({
  items, filters, group, order, lookups, boot, avatarOf, openId, project, onOpen, onPatch, onCreate, onAssign,
}: {
  items: WorkItem[];
  filters: Filters;
  group: GroupKey;
  order: (a: WorkItem, b: WorkItem) => number;
  lookups: Lookups;
  boot: WorkBootstrap;
  avatarOf: AvatarOf;
  openId: string | null;
  project: WorkProject | null;
  onOpen: (id: string) => void;
  onPatch: Patch;
  onCreate: (title: string) => void;
  onAssign: (item: WorkItem, personId: string) => void;
}): JSX.Element {
  const today = todayIso();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [closedSections, setClosedSections] = useState<Set<string>>(new Set());
  const [draft, setDraft] = useState('');

  const kids = useMemo(() => {
    const out = new Map<string, WorkItem[]>();
    for (const i of items) if (i.parent_id) out.set(i.parent_id, [...(out.get(i.parent_id) ?? []), i]);
    return out;
  }, [items]);
  const deepLogged = (i: WorkItem): number => i.logged_hours + (kids.get(i.id) ?? []).reduce((n, k) => n + deepLogged(k), 0);

  // Grouped: flat sections. Otherwise: the tree.
  const grouped = group === 'none' ? null : groupsFor(items.filter((i) => matches(i, filters, { list: true })), group, lookups);
  const tree = useMemo(() => (grouped ? [] : treeRows(items, filters, collapsed, order)), [grouped, items, filters, collapsed, order]);
  const rows = grouped ? grouped.flatMap((g) => [...g.items].sort(order)) : tree.map((r) => r.item);
  const depth = new Map(tree.map((r) => [r.item.id, r]));

  const columns: ColumnDef<WorkItem>[] = [
    {
      key: 'title',
      label: 'Work',
      width: '46%',
      render: (r) => (
        <span className="flex min-w-0 items-center gap-2">
          <TypeMark type={r.type} />
          <Button variant="link" className={r.status === 'done' ? 'text-xs line-through' : 'text-xs'} onClick={() => onOpen(r.id)}>
            {r.key}
          </Button>
          <span className={r.type === 'epic' ? 'truncate font-emphasis text-[var(--fg-strong)]' : 'truncate text-[var(--fg-strong)]'} title={r.title}>{r.title}</span>
          {grouped && r.parent_key ? <span className="shrink-0 text-xs text-[var(--fg-muted)]">in {r.parent_key}</span> : null}
          {isBlocked(r) ? <span className="shrink-0 text-xs font-emphasis text-[var(--warning-fg)]">Blocked</span> : null}
          {r.archived_at ? <span className="shrink-0 text-xs text-[var(--fg-muted)]">Archived</span> : null}
          {r.labels.slice(0, 2).map((l) => <span key={l} className="oly-collabel shrink-0 rounded-[var(--radius-sm)] border border-[var(--border-strong)] px-1.5">{l}</span>)}
        </span>
      ),
      editor: { kind: 'text', maxLength: 500, getValue: (r) => r.title },
      editActivation: 'doubleClick',
    },
    {
      key: 'status',
      label: 'Status',
      render: (r) => <StatusLabel tone={TONE[r.status]}>{STATUS_LABEL[r.status]}</StatusLabel>,
      editor: { kind: 'select', options: STATUSES.map((s) => ({ value: s.key, label: s.label })), getValue: (r) => r.status },
      editActivation: 'click',
    },
    {
      key: 'assignee_id',
      label: 'Assignee',
      render: (r) => {
        const a = avatarOf(assigneeValue(r));
        return a ? (
          <span className="flex items-center gap-1.5"><Avatar size="xs" name={a.name} {...(a.src ? { src: a.src } : {})} decorative />{a.name}</span>
        ) : <span className="text-[var(--fg-muted)]">Unassigned</span>;
      },
      editor: { kind: 'select', nullable: true, options: [], optionsFor: (r) => assigneeOptions(boot, r.project_id, r), getValue: (r) => assigneeValue(r) },
      editActivation: 'click',
    },
    {
      key: 'priority',
      label: 'Priority',
      render: (r) => <span className="flex items-center gap-1.5"><PriorityMark priority={r.priority} />{PRIORITY_LABEL[r.priority]}</span>,
      editor: { kind: 'select', options: PRIORITIES.map(([value, label]) => ({ value, label })), getValue: (r) => r.priority },
      editActivation: 'click',
    },
    {
      key: 'due_date',
      label: 'Due',
      render: (r) => {
        const state = dueState(r, today);
        return r.due_date ? (
          <span className={state === 'overdue' ? 'text-[var(--danger-fg)]' : state === 'today' ? 'text-[var(--warning-fg)]' : ''}>
            {state === 'today' ? 'Today' : shortDate(r.due_date)}
          </span>
        ) : <span className="text-[var(--fg-faint)]">—</span>;
      },
      editor: { kind: 'date', nullable: true, getValue: (r) => r.due_date },
      editActivation: 'click',
    },
    {
      key: 'logged',
      label: 'Logged',
      align: 'right',
      render: (r) => {
        const h = r.type === 'epic' || (kids.get(r.id)?.length ?? 0) > 0 ? deepLogged(r) : r.logged_hours;
        return h ? `${hoursText(h)} h` : <span className="text-[var(--fg-faint)]">—</span>;
      },
    },
  ];

  const save = (row: WorkItem, key: string, value: string | null) => {
    if (key === 'title') {
      const title = (value ?? '').trim();
      if (title && title !== row.title) void onPatch(row, { title });
    } else if (key === 'status' && value) void onPatch(row, { status: value as WorkStatus }, `Moved ${row.key} to ${STATUS_LABEL[value as WorkStatus]}`);
    else if (key === 'priority' && value) void onPatch(row, { priority: value as WorkPriority });
    else if (key === 'due_date') void onPatch(row, { due_date: value || null });
    else if (key === 'assignee_id') void onPatch(row, assigneePatch(value));
  };

  if (rows.length === 0 && items.length > 0 && filtersActive(filters)) {
    return <EmptyState icon={<WorkOrderIcon />} title="Nothing matches" description="No work items match these filters." />;
  }

  return (
    <DataTable<WorkItem>
      columns={columns}
      rows={rows}
      getRowId={(r) => r.id}
      density="compact"
      onCellSave={save}
      highlightedId={openId}
      treeColumn="title"
      rowDepth={(r) => depth.get(r.id)?.depth ?? 0}
      rowExpansion={(r) => {
        const t = depth.get(r.id);
        if (!t?.hasChildren) return null;
        return {
          expanded: t.expanded,
          onToggle: () => setCollapsed((s) => {
            const next = new Set(s);
            if (next.has(r.id)) next.delete(r.id);
            else next.add(r.id);
            return next;
          }),
        };
      }}
      {...(grouped ? {
        sections: grouped.map((g) => ({
          key: g.key,
          label: <span className="font-emphasis text-[var(--fg-strong)]">{g.label} <span className="font-normal text-[var(--fg-muted)]">· {g.items.length} work item{g.items.length === 1 ? '' : 's'}</span></span>,
          rowIds: [...g.items].sort(order).map((i) => i.id),
        })),
        collapsedSections: [...closedSections],
        onSectionToggle: (key: string) => setClosedSections((s) => {
          const next = new Set(s);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          return next;
        }),
      } : {})}
      rowDrop={{
        accepts: (types) => types.includes(PERSON_DRAG),
        onDrop: (row, data) => {
          const personId = data.getData(PERSON_DRAG);
          if (personId) onAssign(row, personId);
        },
      }}
      footerRow={project ? (
        <span className="flex items-center gap-2 text-[var(--fg-muted)]">
          <PlusGlyph />
          <Input
            aria-label="New work item title"
            placeholder={`Add a task to ${project.code} and press Enter`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && draft.trim()) {
                onCreate(draft.trim());
                setDraft('');
              }
            }}
          />
        </span>
      ) : undefined}
      empty={<EmptyState icon={<WorkOrderIcon />} title="No work items yet" description={project ? `Add the first task to ${project.code} below.` : 'Nothing is assigned here yet.'} />}
    />
  );
}
